import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Plus } from 'lucide-react'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CompanyAction,
  getCompanyListColumns,
  useCompanyList,
  useCompanyStore,
} from '@/features/company'
import {
  CompanyDialog,
  DeleteCompanyDialog,
} from '@/features/company/components'
import { useDebounce } from '@/hooks/useDebounce'
import { CompanyStatus, ResponseCompany } from '@/schema'
import { SortOrder } from '@/types/common'
import { cn } from '@/utils/common'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
  SortDirection,
} from '@/utils/constants'
import { buildCompanyRoute } from '@/utils/routes'

const CompanyPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    searchText,
    page,
    sort,
    filters,
    setSearchText,
    setPage,
    setSort,
    setFilter,
    setEditingCompany,
    setDeletingCompany,
    setDialogOpen,
    reset,
  } = useCompanyStore()

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const {
    data: {
      data = [],
      pagination = {
        page: 0,
        total_pages: 0,
        total_items: 0,
        page_size: 0,
        has_next: false,
        has_previous: false,
      },
    } = {},
    isLoading,
    refetch,
  } = useCompanyList({
    search: searchQuery,
    page,
    sort,
    status: filters.status,
  })

  useEffect(() => {
    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!Number.isNaN(page)) {
      refetch()
    }
  }, [page, refetch])

  const handleActionClick = useCallback(
    (action: CompanyAction, item: ResponseCompany) => {
      switch (action) {
        case 'view-detail':
          navigate(buildCompanyRoute(item.id))
          break
        case 'edit':
          setEditingCompany(item)
          break
        case 'delete':
          setDeletingCompany(item)
          break
        default:
          break
      }
    },
    [navigate, setEditingCompany, setDeletingCompany]
  )

  const columns = useMemo(
    () => getCompanyListColumns(handleActionClick, t),
    [handleActionClick, t]
  )

  const handleSortChange = useCallback(
    (colId: keyof ResponseCompany | null, dir: SortOrder) => {
      // Reset to default sorting when both are null (third click)
      if (colId === null && dir === null) {
        setSort({
          column: 'created_at',
          direction: SortDirection.DESC,
        })
      } else {
        setSort({
          column: colId as keyof ResponseCompany,
          direction: dir,
        })
      }
    },
    [setSort]
  )

  const noDataMessage = useMemo(() => {
    if (searchQuery) {
      return <EmptySearchResult translationKey="company" />
    }
    return null
  }, [searchQuery])

  return (
    <div className="flex flex-col space-y-6">
      {/* MARK: Page Title */}
      <div className="flex justify-between flex-shrink-0">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('company.page.title')}
          </h1>
          <p className="m-0 text-md text-muted-foreground">
            {t('company.page.description')}
          </p>
        </div>
        <div>
          <Button
            onClick={() => setDialogOpen(true)}
            className="text-sm border border-solid border-transparent min-w-[120px]"
            leadingIcon={<Plus size={20} />}
          >
            {t('company.actions.addNew')}
          </Button>
        </div>
      </div>

      {/* MARK: Search */}
      <div className="flex flex-shrink-0 gap-3">
        <div className="flex-1">
          <InputSearch
            searchText={searchText}
            setSearchText={setSearchText}
            placeholder={t('company.filters.search')}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outlined"
              className="justify-between text-primary-900"
            >
              {filters.status
                ? t(`company.status.${filters.status}`)
                : t('company.filters.status')}
              <ChevronDown
                size={16}
                className="ml-2 text-foreground-fg-quaternary-400"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="!min-w-[120px]">
            <DropdownMenuItem 
              onClick={() => setFilter('status', undefined)}
              className={cn(filters.status === undefined && 'bg-gray-100 font-medium')}
            >
              {t('company.filters.allStatus')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setFilter('status', CompanyStatus.ACTIVE)}
              className={cn(filters.status === CompanyStatus.ACTIVE && 'bg-gray-100 font-medium')}
            >
              {t('company.status.active')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setFilter('status', CompanyStatus.ARCHIVED)}
              className={cn(filters.status === CompanyStatus.ARCHIVED && 'bg-gray-100 font-medium')}
            >
              {t('company.status.archived')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* MARK: Paginated Table */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Table
          columns={columns}
          data={data ?? []}
          page={(pagination?.page ?? DEFAULT_PAGE) - 1}
          totalPages={pagination?.total_pages ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          noDataMessage={noDataMessage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={(row) => handleActionClick('view-detail', row)}
          roundedTop
        />
      </div>

      {/* Dialog components */}
      <CompanyDialog />
      <DeleteCompanyDialog
        currentItemCount={data?.length ?? 0}
        totalItems={pagination?.total_items ?? 0}
      />
    </div>
  )
}

export default CompanyPage
